/**
 * MODULE: domain/pages/frontend/api/pagesCallPreviewApi.ts
 * PURPOSE: Gateway client for call transcript preview blocks (calls + transcript-analysis)
 */

import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type { PageCallListItem, PageCallTranscriptPreview } from '../../contracts/pages.types';
import {
  normalizeCallListItem,
  normalizeCallTranscriptPreview,
} from '../utils/pageCallPreviewUtils';

const CALLS_BASE = `${API_CONFIG.baseURL}/api/v1/calls`;
const ANALYSIS_BASE = `${API_CONFIG.baseURL}/api/v1/transcript-analysis`;

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => 'Request failed');
    throw new Error(detail || `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export const pagesCallPreviewApi = {
  async listRecentCalls(limit = 20): Promise<PageCallListItem[]> {
    const response = await fetchJson<{ calls?: unknown[] }>(
      `${CALLS_BASE}?per_page=${encodeURIComponent(String(limit))}&page=1`
    );
    const calls = Array.isArray(response.calls) ? response.calls : [];
    return calls
      .map((call) => normalizeCallListItem(call as Record<string, unknown>))
      .filter((item): item is PageCallListItem => item != null);
  },

  async getCallTranscriptPreview(callId: string): Promise<PageCallTranscriptPreview> {
    const id = callId.trim();
    const call = await fetchJson<Record<string, unknown>>(`${CALLS_BASE}/${encodeURIComponent(id)}`);

    let analysis: Record<string, unknown> | null = null;
    try {
      analysis = await fetchJson<Record<string, unknown>>(
        `${ANALYSIS_BASE}/${encodeURIComponent(id)}`
      );
    } catch (error) {
      if (!(error instanceof Error) || !/404|not found/i.test(error.message)) {
        throw error;
      }
    }

    return normalizeCallTranscriptPreview(call, analysis);
  },
};
